import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { Product } from '@/types';
import { formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { WishlistSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';
import { DiscountBadge } from '@/components/ui/discount-badge';
import { toast } from 'react-hot-toast';

export function ModernWishlistPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const wishlist = useStore((state) => state.wishlist);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const addToCart = useStore((state) => state.addToCart);
  const { storeInfo, openLockNotice } = useStoreInfo();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products/visible?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          const wishlistProducts = data.products.filter((p: Product) => 
            wishlist.includes(p.id) && p.status === 1
          );
          setProducts(wishlistProducts);
        }
      } catch (error) {
        console.error('Error fetching wishlist:', error);
      } finally {
        setLoading(false);
      }
    };

    if (wishlist.length > 0) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [wishlist]);

  const handleAddToCart = (product: Product) => {
    if (storeInfo?.site_lock === 1) {
      openLockNotice();
      return;
    }
    addToCart({
      ...product,
      quantity: 1,
      selectedVariations: []
    });
    toast.success('Added to cart!');
  };

  const handleRemoveFromWishlist = (productId: number) => {
    toggleWishlist(productId);
    // Remove from local products state immediately for instant feedback
    setProducts(prev => prev.filter(p => p.id !== productId));
    toast.success('Removed from wishlist');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-display font-bold text-gray-900 mb-6">My Wishlist</h1>
          <WishlistSkeleton />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <>
        <Helmet>
          <title>My Wishlist</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-24 md:pb-8">
          <div className="text-center px-4">
            <div className="w-24 h-24 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-12 w-12 text-brand-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-600 mb-8">Save items you love for later!</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`My Wishlist (${products.length})`}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-display font-bold text-gray-900">
              My Wishlist ({products.length} {products.length === 1 ? 'item' : 'items'})
            </h1>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((product, index) => {
              const discountInfo = calculateProductDiscount(product, 1);
              
              return (
                <motion.div
                  key={product.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="product-card"
                >
                  <div className="card group relative">
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="aspect-square relative overflow-hidden">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.produt_name}
                            className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <NoImagePlaceholder 
                            className="w-full h-full rounded-none" 
                            size="lg"
                            text="No Image"
                          />
                        )}
                        {product.postType === "1" && (
                          <div className="absolute top-2 right-2 bg-accent-primary text-white px-2 py-1 rounded-full text-xs font-semibold">
                            Pre-Order
                          </div>
                        )}
                        {discountInfo.hasDiscount && (
                          <div className="absolute top-2 left-2">
                            <DiscountBadge 
                              discountType={discountInfo.discountType}
                              discountAmount={discountInfo.discountAmount}
                            />
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="p-4">
                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-medium text-gray-900 mb-1 group-hover:text-brand-600 transition-colors line-clamp-2 text-sm">
                          {product.produt_name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                          {product.category || 'Uncategorized'}
                        </p>
                        <div className="mb-3">
                          <span className="text-lg font-bold text-brand-600">
                            {formatPrice(product.sellingprice)}
                          </span>
                        </div>
                      </Link>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleRemoveFromWishlist(product.id)}
                          className="flex items-center justify-center gap-1 py-2 px-3 bg-brand-50 text-brand-600 rounded-lg hover:bg-brand-100 transition-colors text-xs font-medium"
                        >
                          <Trash2 className="h-3 w-3" />
                          Remove
                        </button>
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={storeInfo?.site_lock === 1}
                          title={storeInfo?.site_lock === 1 ? 'Site Locked' : undefined}
                          className="flex items-center justify-center gap-1 py-2 px-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-xs font-medium"
                        >
                          <ShoppingCart className="h-3 w-3" />
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
